import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import * as yup from 'yup';

export const ServicesPage: React.FC = () => {
  const [selectedService, setSelectedService] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view' | 'delete'>('create');
  const [tab, setTab] = useState<'services' | 'categories'>('services');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const queryClient = useQueryClient();

  // Загрузка данных
  const { data: servicesData, isLoading, error, refetch } = useQuery(['services'], async () => {
    const res = await apiClient.get('/api/services');
    return res.data;
  }, { refetchInterval: 30000 });

  // Категории услуг
  const { data: categoriesData } = useQuery(['serviceCategories'], async () => {
    const res = await apiClient.get('/api/services/categories');
    return res.data;
  });

  // Мутации
  const createServiceMutation = useMutation(
    (serviceData: any) => apiClient.post('/api/services', serviceData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Услуга создана', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка создания', severity: 'error' });
      },
    }
  );

  const updateServiceMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => apiClient.put(`/api/services/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Услуга обновлена', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка обновления', severity: 'error' });
      },
    }
  );

  const deleteServiceMutation = useMutation(
    (id: string) => apiClient.delete(`/api/services/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['services']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Услуга удалена', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
      },
    }
  );

  // Схема валидации
  const serviceValidationSchema = yup.object({
    name: yup.string().min(3, 'Минимум 3 символа').required('Название обязательно'),
    description: yup.string().min(10, 'Минимум 10 символов').required('Описание обязательно'),
    estimatedDuration: yup.number().min(1, 'Минимум 1 минута').max(480, 'Максимум 8 часов').required('Укажите время'),
    categoryId: yup.string().required('Выберите категорию'),
  });

  // Форма
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      estimatedDuration: 15,
      categoryId: '',
      requiredDocuments: [],
    },
    validationSchema: serviceValidationSchema,
    onSubmit: (values) => {
      if (dialogType === 'create') {
        createServiceMutation.mutate(values);
      } else if (dialogType === 'edit' && selectedService) {
        updateServiceMutation.mutate({ id: selectedService.id, data: values });
      }
    },
  });

  const handleOpenDialog = (type: 'create' | 'edit' | 'view' | 'delete', service?: any) => {
    setDialogType(type);
    setSelectedService(service || null);
    
    if (type === 'edit' && service) {
      formik.setValues({
        name: service.name,
        description: service.description,
        estimatedDuration: service.estimated_duration,
        categoryId: service.category_id || '',
        requiredDocuments: service.required_documents || [],
      });
    } else if (type === 'create') {
      formik.resetForm();
      const categories = categoriesData?.data?.categories || [];
      if (categories.length > 0) {
        formik.setValues({
          name: '',
          description: '',
          estimatedDuration: 15,
          categoryId: categories[0].id,
          requiredDocuments: [],
        });
      }
    }
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedService(null);
    formik.resetForm();
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Управление услугами</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ошибка загрузки услуг</Alert>
      </Box>
    );
  }

  const services = servicesData?.data?.services || [];

  return (
    <>
      <Helmet>
        <title>Управление услугами - ELEKTROM</title>
      </Helmet>
      
      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Управление услугами
        </Typography>
            <Typography variant="body1" color="text.secondary">
              Настройка услуг, требований и времени обслуживания
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить данные">
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Создать услугу
            </Button>
          </Box>
        </Box>

        {/* Статистические карточки */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Всего услуг</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{services.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>в каталоге</Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Активных</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                      {services.filter((s: any) => s.is_active).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>услуг</Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Среднее время</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                      {Math.round(services.reduce((sum: number, s: any) => sum + (s.estimated_duration || 0), 0) / services.length) || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>минут</Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Табы */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label="Услуги"
            color={tab === 'services' ? 'primary' : 'default'}
            onClick={() => setTab('services')}
            sx={{ mr: 1 }}
          />
          <Chip
            label="Категории"
            color={tab === 'categories' ? 'primary' : 'default'}
            onClick={() => setTab('categories')}
          />
        </Box>

        {/* Таблица услуг */}
        <Card>
          <CardContent>
            {tab === 'services' && (
              <Typography variant="h6" gutterBottom>
                Каталог услуг
              </Typography>
            )}
            {tab === 'services' && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Услуга</TableCell>
                    <TableCell>Категория</TableCell>
                    <TableCell align="center">Время</TableCell>
                    <TableCell align="center">Документы</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service: any) => (
                    <TableRow key={service.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {service.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {service.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={service.category_name || 'Без категории'}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="info.main">
                          {service.estimated_duration} мин
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={service.required_documents?.join(', ') || 'Документы не требуются'}>
                          <Chip
                            label={service.required_documents?.length || 0}
                            color={service.required_documents?.length ? 'warning' : 'default'}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={service.is_active ? 'Активна' : 'Неактивна'}
                          color={service.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Просмотр">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('view', service)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Редактировать">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', service)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDialog('delete', service)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {tab === 'categories' && (
              <Box>
                <Typography variant="h6" gutterBottom>Категории услуг</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Новая категория"
                    value={formik.values.categoryId}
                    onChange={(e) => {
                      // временно используем categoryId поле формы как буфер для имени новой категории
                      formik.setFieldValue('categoryId', e.target.value);
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={async () => {
                      const name = String(formik.values.categoryId || '').trim();
                      if (!name) return;
                      try {
                        await apiClient.post('/api/services/categories', { name });
                        await queryClient.invalidateQueries(['serviceCategories']);
                        formik.setFieldValue('categoryId', '');
                        setSnackbar({ open: true, message: 'Категория добавлена', severity: 'success' });
                      } catch (e) {
                        setSnackbar({ open: true, message: 'Не удалось добавить категорию', severity: 'error' });
                      }
                    }}
                  >
                    Добавить
                  </Button>
                </Box>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Название</TableCell>
                      <TableCell>Описание</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(categoriesData?.data?.categories || []).map((cat: any) => (
                      <TableRow key={cat.id}>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell>{cat.description}</TableCell>
                        <TableCell align="right">
                          <Button
                            color="error"
                            size="small"
                            onClick={async () => {
                              try {
                                await apiClient.delete(`/api/services/categories/${cat.id}`);
                                await queryClient.invalidateQueries(['serviceCategories']);
                                setSnackbar({ open: true, message: 'Категория удалена', severity: 'success' });
                              } catch (e) {
                                setSnackbar({ open: true, message: 'Нельзя удалить категорию с услугами', severity: 'error' });
                              }
                            }}
                          >
                            Удалить
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Диалог создания/редактирования/просмотра/удаления */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogType === 'create' && 'Создание новой услуги'}
            {dialogType === 'edit' && 'Редактирование услуги'}
            {dialogType === 'view' && 'Информация об услуге'}
            {dialogType === 'delete' && 'Подтверждение удаления'}
          </DialogTitle>
          
          {/* Оборачиваем содержимое в form, чтобы работал submit */}
          <form onSubmit={formik.handleSubmit} noValidate>
          <DialogContent dividers>
            {dialogType === 'delete' ? (
              <Alert severity="warning">
                Вы действительно хотите удалить услугу "{selectedService?.name}"?
                Это действие нельзя отменить.
              </Alert>
            ) : dialogType === 'view' && selectedService ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>{selectedService.name}</Typography>
                  <Typography variant="body1" paragraph>{selectedService.description}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Категория:</Typography>
                  <Typography>{selectedService.category_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Время обслуживания:</Typography>
                  <Typography>{selectedService.estimated_duration} минут</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Необходимые документы:
          </Typography>
                  {selectedService.required_documents?.length ? (
                    <List dense>
                      {selectedService.required_documents.map((doc: string, index: number) => (
                        <ListItem key={index}>
                          <ListItemText primary={doc} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary">Документы не требуются</Typography>
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Название услуги"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                    placeholder="Например: Оформление паспорта"
                    disabled={dialogType === 'view'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Время обслуживания (мин)"
                    name="estimatedDuration"
                    value={formik.values.estimatedDuration}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.estimatedDuration && Boolean(formik.errors.estimatedDuration)}
                    helperText={formik.touched.estimatedDuration && formik.errors.estimatedDuration}
                    inputProps={{ min: 1, max: 480 }}
                    disabled={dialogType === 'view'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={dialogType === 'view'}>
                    <InputLabel id="category-label">Категория</InputLabel>
                    <Select
                      labelId="category-label"
                      id="categoryId"
                      name="categoryId"
                      value={formik.values.categoryId}
                      label="Категория"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                    >
                      {(categoriesData?.data?.categories || []).map((cat: any) => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Описание услуги"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    placeholder="Подробное описание услуги..."
                    disabled={dialogType === 'view'}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {dialogType === 'view' ? 'Закрыть' : 'Отмена'}
            </Button>
            {dialogType === 'delete' && (
              <Button
                color="error"
                variant="contained"
                onClick={() => selectedService && deleteServiceMutation.mutate(selectedService.id)}
                disabled={deleteServiceMutation.isLoading}
              >
                {deleteServiceMutation.isLoading ? 'Удаление...' : 'Удалить'}
              </Button>
            )}
            {(dialogType === 'create' || dialogType === 'edit') && (
              <Button
                type="submit"
                variant="contained"
                disabled={createServiceMutation.isLoading || updateServiceMutation.isLoading}
              >
                {createServiceMutation.isLoading || updateServiceMutation.isLoading
                  ? 'Сохранение...' 
                  : dialogType === 'create' ? 'Создать услугу' : 'Сохранить изменения'
                }
              </Button>
            )}
          </DialogActions>
          </form>
        </Dialog>
        
        {/* Уведомления */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
